import {FormEvent, useState} from 'react';
import {Link, useNavigate, useParams} from 'react-router-dom';
import {ApiError} from '../api/client';
import {usePlant, usePlantMutations} from '../api/plants';
import {usePlants} from '../api/posts';
import Button from '../components/Button';
import EmptyState from '../components/feedback/EmptyState';
import ErrorBox, {FieldError, isGeneralError} from '../components/feedback/ErrorBox';
import Sheet from '../components/feedback/Sheet';
import {SkeletonBlock, SkeletonList} from '../components/feedback/Skeleton';
import Icon from '../components/Icon';
import SeasonStory, {useSeasonFeed} from '../components/plant/SeasonStory';
import {confirm} from '../hooks/useConfirm';
import {useFormDraft} from '../hooks/useFormDraft';
import {toast} from '../hooks/useToast';
import {speciesKeyOf} from '../lib/plants';
import {formatDayMonth} from '../lib/when';
import {PlantSummary} from '../types';

// One plant, every season (wireframe "plant"): season chips, a summary line, then the season's
// tells as the lifecycle (a crop) or day by day (a permanent plant), the previous season for
// comparison, the permanent-plant toggle, and Rename (which offers to merge into a plant that
// already has the new name). Plantings are derived here as plant × season.

const tellsWord = (n: number) => `${n} ${n === 1 ? 'tell' : 'tells'}`;

function RenameSheet({plant, onClose}: {plant: PlantSummary; onClose: () => void}) {
    const navigate = useNavigate();
    const {rename} = usePlantMutations();
    const form = useFormDraft(`plant:${plant.id}`, {label: plant.label}, {label: `Renaming ${plant.label}`, route: `/plants/${plant.id}`, listed: false});
    const label = form.values.label.trim();

    const done = (result: {id: string; merged: boolean}) => {
        form.clear();
        onClose();
        toast.success(result.merged ? `Merged into ${label}.` : 'Renamed.');
        if (result.id !== plant.id) navigate(`/plants/${result.id}`, {replace: true});
    };

    const submit = (event: FormEvent) => {
        event.preventDefault();
        rename.mutate(
            {id: plant.id, label},
            {
                onSuccess: done,
                onError: async error => {
                    if (!(error instanceof ApiError) || error.status !== 409) return;
                    rename.reset();
                    await confirm({
                        title: `Merge into ${label}?`,
                        body: `There is already a plant called ${label}. ${plant.label}'s ${tellsWord(plant.tellCount)} move to it, and the name ${plant.label} goes.`,
                        help: 'Each tell keeps its words, photos, place and date; a tell that already names both keeps one.',
                        confirmLabel: 'Merge',
                        busyLabel: 'Merging…',
                        action: () => rename.mutateAsync({id: plant.id, label, merge: true}).then(done)
                    });
                }
            }
        );
    };

    return (
        <Sheet open onClose={onClose} title="Rename this plant" busy={rename.isPending}>
            <form onSubmit={submit} className="flex flex-col gap-5">
                <label className="flex flex-col gap-1">
                    <span className="meta">Name, then the variety after a ·</span>
                    <input className="field" type="text" maxLength={123} autoFocus required value={form.values.label} onChange={e => form.set({label: e.target.value})} placeholder="Garlic · Takahue" />
                    <FieldError error={rename.error} field="label" />
                </label>
                <p className="meta m-0">Every tell that names it changes with it. A name you already use merges the two.</p>
                {isGeneralError(rename.error) && <ErrorBox error={rename.error} />}
                <Button type="submit" busy={rename.isPending} busyLabel="Saving…" disabled={label === '' || label === plant.label}>
                    Save
                </Button>
            </form>
        </Sheet>
    );
}

export default function PlantPage() {
    const {id} = useParams<{id: string}>();
    const navigate = useNavigate();
    const detail = usePlant(id);
    const all = usePlants();
    const {setPermanent} = usePlantMutations();
    const [renaming, setRenaming] = useState(false);

    const plant = detail.data?.plant;
    const state = useSeasonFeed(detail.data?.seasons ?? [], {plantId: id});
    const isPermanent = plant?.isPermanent ?? false;
    const firstPlace = state.posts.find(p => p.place)?.place ?? null;
    // Back to the species page when this is one of several varieties of the same name.
    const speciesKey = plant ? speciesKeyOf(plant.name) : null;
    const siblings = speciesKey ? (all.data ?? []).filter(p => speciesKeyOf(p.name) === speciesKey).length : 0;

    const addTell = () => {
        if (!plant) return;
        const q = new URLSearchParams({plant: plant.label});
        if (firstPlace) q.set('place', firstPlace.id);
        navigate(`/tell/new?${q.toString()}`);
    };

    return (
        <div className="settle-in flex flex-col gap-5">
            <header className="pt-2">
                {siblings > 1 && plant ? (
                    <Link to={`/plants/species/${encodeURIComponent(speciesKey!)}`} className="meta mb-1 inline-flex items-center gap-1 no-underline">
                        <Icon name="back" size={16} /> {plant.name}
                    </Link>
                ) : (
                    <Link to="/plants" className="meta mb-1 inline-flex items-center gap-1 no-underline">
                        <Icon name="back" size={16} /> Plants
                    </Link>
                )}
                {plant ? (
                    <>
                        <h1 className="display m-0">{plant.name}</h1>
                        {plant.variety && <p className="meta mt-1">{plant.variety}</p>}
                    </>
                ) : (
                    <SkeletonBlock className="h-9 w-1/2" />
                )}
            </header>

            {detail.isPending && <SkeletonList rows={4} />}
            {detail.isError && <ErrorBox error={detail.error} onRetry={() => void detail.refetch()} />}

            {detail.isSuccess && state.seasons.length === 0 && (
                <EmptyState icon="leaf" tone="plain" title="Nothing recorded for this plant yet.">
                    Its tells will line up here season by season.
                </EmptyState>
            )}

            {plant && (
                <SeasonStory
                    state={state}
                    isPermanent={isPermanent}
                    summary={season => [plant.variety, firstPlace ? null : season.placeName, `started ${formatDayMonth(season.firstAt)}`, tellsWord(season.tells)].filter(Boolean).join(' · ')}
                    placeLink={firstPlace}
                />
            )}

            {plant && (
                <section className="flex flex-col gap-2">
                    {plant.looksPermanent && !plant.isPermanent && <div className="nudge px-3 py-2">Looks like a permanent plant. Mark it so?</div>}
                    <div className="rows rounded-[18px] border-[1.5px] border-line bg-surface">
                        <div className="flex min-h-[64px] items-center gap-3 px-4 py-2">
                            <Icon name="tree" size={20} className="text-shoot-text" />
                            <span className="min-w-0 flex-1">
                                <span className="block font-[650]">Permanent plant</span>
                                <span className="meta block">
                                    {plant.isPermanent ? 'Trees and bushes: one timeline a year' : plant.looksPermanent ? `Looks like one: tells across ${plant.years} years, planted once` : 'Trees, bushes, perennials'}
                                </span>
                            </span>
                            <Button variant="secondary" size="sm" busy={setPermanent.isPending} onClick={() => setPermanent.mutateAsync({id: plant.id, isPermanent: !plant.isPermanent})}>
                                {plant.isPermanent ? 'Not permanent' : 'Mark permanent'}
                            </Button>
                        </div>
                        <button type="button" className="flex min-h-[56px] w-full items-center gap-3 px-4 text-left" onClick={() => setRenaming(true)}>
                            <Icon name="edit" size={20} className="text-shoot-text" />
                            <span className="min-w-0 flex-1">
                                <span className="block font-[650]">Rename</span>
                                <span className="meta block">Fix a spelling, add the variety, or merge with another name</span>
                            </span>
                            <Icon name="chevron" size={20} className="text-ink-3" />
                        </button>
                    </div>
                </section>
            )}

            {plant && (
                <Button variant="secondary" onClick={addTell}>
                    <Icon name="plus" size={20} /> Add a tell
                </Button>
            )}

            {renaming && plant && <RenameSheet key={plant.id} plant={plant} onClose={() => setRenaming(false)} />}
        </div>
    );
}
